import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, fmtDate, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
    Compass, ArrowLeft, Plus, Package, Clock, Car, Home as HomeIcon,
    Sparkles, CheckCircle2, Trash2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const LINK_META = {
    assets: { icon: Package, tone: "bg-stone-100 text-stone-700 border-stone-200", href: (d) => `/assets/${d.id}`, label: "Asset" },
    renewals: { icon: Clock, tone: "bg-amber-50 text-amber-800 border-amber-200", href: () => `/renewals`, label: "Renewal" },
    vehicles: { icon: Car, tone: "bg-sky-50 text-sky-700 border-sky-200", href: (d) => `/vehicles/${d.id}`, label: "Vehicle" },
    home_projects: { icon: HomeIcon, tone: "bg-emerald-50 text-emerald-700 border-emerald-200", href: () => `/home-hub`, label: "Home project" },
    timeline: { icon: Sparkles, tone: "bg-violet-50 text-violet-700 border-violet-200", href: () => `/timeline`, label: "Timeline moment" },
};

export default function JourneyDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { hasRole } = useAuth();
    const canEdit = hasRole && hasRole("admin", "finance", "expense_user");
    const canArchive = hasRole && hasRole("admin", "finance");
    const [j, setJ] = useState(null);
    const [newTask, setNewTask] = useState("");
    const [busy, setBusy] = useState(false);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [cascade, setCascade] = useState(true);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/journeys/${id}`);
            setJ(data);
        } catch (err) {
            toast.error(formatApiError(err));
            nav("/journeys");
        }
    }, [id, nav]);
    useEffect(() => { load(); }, [load]);

    const toggle = async (taskId, done) => {
        try {
            await api.post(`/journeys/${id}/tasks/${taskId}/toggle`, { done });
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const addTask = async (e) => {
        e.preventDefault();
        if (!newTask.trim()) return;
        setBusy(true);
        try {
            await api.post(`/journeys/${id}/tasks`, { title: newTask });
            setNewTask("");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const markDone = async () => {
        try {
            await api.patch(`/journeys/${id}`, { status: "completed" });
            toast.success("Journey completed. Well done.");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const archive = async () => {
        setBusy(true);
        try {
            const { data } = await api.delete(`/journeys/${id}${cascade ? "?cascade=true" : ""}`);
            const c = data.cascaded || {};
            const total = Object.values(c).reduce((a, b) => a + b, 0);
            toast.success(cascade && total > 0
                ? `Journey archived. Also archived ${total} linked items.`
                : "Journey archived.");
            nav("/journeys");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    if (!j) return <div className="text-sm text-stone-500 p-8" data-testid="journey-detail-loading">Loading journey…</div>;

    const tasks = j.tasks || [];
    const linked = j.linked || {};
    const totalCreated = Object.values(j.creation_summary || {}).reduce((a, b) => a + b, 0);

    return (
        <div data-testid="journey-detail-page">
            <Link to="/journeys" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-stone-500 hover:text-stone-900 mb-4" data-testid="journey-back-link">
                <ArrowLeft size={12} /> All journeys
            </Link>

            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Compass size={12} /> Life Moment</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">{j.title}</h1>
                    <p className="text-sm text-stone-500 mt-3">
                        {j.template_title} · Started {fmtDate(j.start_date)}
                        {j.budget != null && <> · Budget {fmtMoney(j.budget, j.currency)}</>}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {canEdit && j.status === "active" && (
                        <Button onClick={markDone} variant="outline" data-testid="journey-mark-done">
                            <CheckCircle2 size={13} className="mr-1.5" /> Mark done
                        </Button>
                    )}
                    {canArchive && (
                        <Button onClick={() => setArchiveOpen(true)} variant="outline" className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800" data-testid="journey-archive">
                            <Trash2 size={13} className="mr-1.5" /> Archive
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="rounded-lg border border-stone-200 bg-white p-5" data-testid="journey-progress">
                    <div className="eyebrow mb-3">Progress</div>
                    <div className="flex items-baseline justify-between">
                        <div className="font-display text-4xl text-stone-900 leading-none">{j.progress_pct}%</div>
                        <div className="text-[12px] text-stone-500">{j.task_done}/{j.task_total} tasks</div>
                    </div>
                    <div className="mt-3 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                        <div className="h-full bg-stone-900 transition-all" style={{ width: `${j.progress_pct}%` }} />
                    </div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-5">
                    <div className="eyebrow mb-3">Auto-created</div>
                    <div className="font-display text-4xl text-stone-900 leading-none">{totalCreated}</div>
                    <div className="text-[12px] text-stone-500 mt-2 leading-relaxed">
                        {Object.entries(j.creation_summary || {}).filter(([, n]) => n).map(([k, n]) => `${n} ${k.replace("_", " ")}`).join(" · ") || "Nothing yet"}
                    </div>
                </div>
                <div className="rounded-lg border border-stone-200 bg-white p-5">
                    <div className="eyebrow mb-3">Status</div>
                    <div className="font-display text-2xl text-stone-900 leading-tight capitalize">{j.status}</div>
                    {j.notes && <div className="text-[12px] text-stone-500 mt-2 line-clamp-3">{j.notes}</div>}
                </div>
            </div>

            {/* Tasks */}
            <section className="mb-10">
                <div className="eyebrow mb-3">Task checklist</div>
                <div className="rounded-lg border border-stone-200 bg-white divide-y divide-stone-100" data-testid="journey-tasks">
                    {tasks.map((t) => (
                        <div key={t.id} className="flex items-center gap-3 px-4 py-3 group" data-testid={`journey-task-${t.id}`}>
                            <Checkbox
                                checked={!!t.done}
                                onCheckedChange={(v) => canEdit && toggle(t.id, !!v)}
                                disabled={!canEdit}
                                data-testid={`journey-task-toggle-${t.id}`}
                            />
                            <div className={`flex-1 text-sm ${t.done ? "line-through text-stone-400" : "text-stone-900"}`}>
                                {t.title}
                            </div>
                            {t.done && t.done_at && (
                                <div className="text-[11px] text-stone-400 tabular-nums">{fmtDate(t.done_at)}</div>
                            )}
                        </div>
                    ))}
                    {tasks.length === 0 && (
                        <div className="text-sm text-stone-500 px-4 py-8 text-center">No tasks. Add one below.</div>
                    )}
                    {canEdit && (
                        <form onSubmit={addTask} className="flex items-center gap-2 px-4 py-3">
                            <Plus size={14} className="text-stone-400" />
                            <Input
                                value={newTask}
                                onChange={(e) => setNewTask(e.target.value)}
                                placeholder="Add a task…"
                                className="border-0 shadow-none focus-visible:ring-0 px-0"
                                data-testid="journey-new-task-input"
                            />
                            <Button type="submit" size="sm" variant="outline" disabled={busy || !newTask.trim()} data-testid="journey-new-task-btn">
                                Add
                            </Button>
                        </form>
                    )}
                </div>
            </section>

            {/* Linked entities */}
            {Object.values(linked).some((arr) => arr.length > 0) && (
                <section data-testid="journey-linked">
                    <div className="eyebrow mb-3">What we created</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(linked).map(([key, arr]) => {
                            if (!arr.length) return null;
                            const meta = LINK_META[key] || LINK_META.assets;
                            const Icon = meta.icon;
                            return (
                                <div key={key} className="rounded-lg border border-stone-200 bg-white p-4" data-testid={`journey-linked-${key}`}>
                                    <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border mb-2 ${meta.tone}`}>
                                        <Icon size={11} /> {meta.label} · {arr.length}
                                    </div>
                                    <div className="space-y-1.5">
                                        {arr.map((d) => (
                                            <Link
                                                key={d.id}
                                                to={meta.href(d)}
                                                className="block text-sm text-stone-900 hover:text-stone-600 truncate"
                                            >
                                                {d.name || d.title || d.label || "Entry"}
                                                {d.expiry_date && (
                                                    <span className="text-[11px] text-stone-500 ml-2">
                                                        exp {fmtDate(d.expiry_date)}
                                                    </span>
                                                )}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Archive dialog — offers cascade archive */}
            <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
                <DialogContent className="max-w-md" data-testid="journey-archive-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Archive this journey?</DialogTitle>
                        <DialogDescription className="text-[13px] text-stone-500">
                            The journey will move to the completed list. You can optionally also archive
                            everything this journey created so your dashboard stays clean.
                        </DialogDescription>
                    </DialogHeader>
                    <label className="flex items-start gap-3 rounded-lg border border-stone-200 bg-stone-50 p-3 cursor-pointer" data-testid="journey-archive-cascade-label">
                        <Checkbox
                            checked={cascade}
                            onCheckedChange={(v) => setCascade(!!v)}
                            data-testid="journey-archive-cascade"
                        />
                        <div>
                            <div className="text-sm font-medium text-stone-900">Also archive linked items</div>
                            <div className="text-[12px] text-stone-500 mt-0.5">
                                Assets, vehicles, renewals and the timeline event created by this journey will be archived too.
                                Anything you edited later stays.
                            </div>
                        </div>
                    </label>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setArchiveOpen(false)} disabled={busy}>Cancel</Button>
                        <Button
                            onClick={archive}
                            disabled={busy}
                            className="bg-red-700 hover:bg-red-800 text-white"
                            data-testid="journey-archive-confirm"
                        >
                            <Trash2 size={13} className="mr-1.5" />
                            {busy ? "Archiving…" : cascade ? "Archive everything" : "Archive journey only"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
